import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import config from '@iotv/config';
import { getNiceDate, Incident, IncidentClassName, IncidentStatus, IncidentTypeNames, Severity } from '@iotv/datamodel-core';
import { isValidBusinessObject } from '@iotv/iotv-v3-types';
import CheckIcon from '@mui/icons-material/Check';
import WarningIcon from '@mui/icons-material/Warning';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Stack } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ConsumerDao } from '../../data/ConsumerDAO';
import { DataGridRadioColumn } from '../../util/DataGrid/DataGridHelpers';
import { IncidentView } from '../Notification/Incidents/IncidentView';
const dynamo = config.aws.dynamoDB;
const columnLayout = {
    display: 'flex',
    resizable: false,
    sortable: false,
    disableColumnMenu: true,
};
const columns = [
    DataGridRadioColumn,
    {
        field: 'status',
        headerName: 'Status',
        headerAlign: 'center',
        ...columnLayout,
        width: 65,
        display: 'flex',
        align: 'center',
        type: 'custom',
        renderCell(params) {
            let StatusIcon = undefined;
            switch (params.value) {
                case IncidentStatus.OPEN:
                    StatusIcon = _jsx(WarningIcon, { color: "warning" });
                    break;
                case IncidentStatus.CLOSED:
                    StatusIcon = _jsx(CheckIcon, { color: "success" });
                    break;
            }
            return StatusIcon;
        },
    },
    {
        field: 'severity',
        headerName: 'Severity',
        headerAlign: 'center',
        ...columnLayout,
        minWidth: 150,
        display: 'flex',
        align: 'center',
        type: 'custom',
        renderCell(params) {
            // handle typo in enum (now fixed)
            const value = params.value === 'MEDUIM' ? 'MEDIUM' : params.value;
            const severity = (value ?? Severity.NONE);
            const severities = Object.keys(Severity).reverse();
            const index = severities.findIndex((s) => s === severity);
            const level = 0 <= index ? index : 0;
            const icons = [];
            for (let i = 0; i < level; i++) {
                icons.push(_jsx(WarningIcon, { color: params.row.status === IncidentStatus.CLOSED ? 'success' : 'warning' }));
            }
            for (let i = level; i < severities.length - 1; i++) {
                icons.push(_jsx(WarningAmberIcon, { sx: { opacity: 0.6 } }));
            }
            return _jsxs(Stack, { direction: "row", children: [...icons] });
        },
    },
    {
        field: 'incidentType',
        headerName: 'Type',
        minWidth: 120,
        ...columnLayout,
        valueFormatter: (value) => (value ? IncidentTypeNames[value] : value),
    },
    {
        field: 'timestamp',
        headerName: 'Opened',
        ...columnLayout,
        minWidth: 160,
        valueFormatter: (value) => (value && getNiceDate(value)) ?? value,
    },
    {
        field: 'lastUpdated',
        headerName: 'Updated',
        ...columnLayout,
        minWidth: 160,
        valueFormatter: (value) => (value && getNiceDate(value)) ?? value,
    },
    {
        field: 'closedTimestamp',
        headerName: 'Closed',
        ...columnLayout,
        minWidth: 160,
        valueFormatter: (value) => (value && getNiceDate(value)) ?? value,
    },
    {
        field: 'summary',
        headerName: 'Summary',
        ...columnLayout,
        flex: 1,
    },
];
export const IncidentListCard = ({ viewObject, contextCustomer, user, userGroupRoles, userSelectedGroup, roleArbitrationInstance, userFunctions, transactionFunctions, isSmallView, }) => {
    // Error: Cannot access refs during render
    'use no memo';
    const { matchedPrimary } = viewObject;
    const [loading, setLoading] = useState(false);
    const [incidents, setIncidents] = useState([]);
    // database pagination querying
    const mapPageToNextCursor = useRef({ 0: undefined });
    // pagination state
    const pageSize = 10;
    const [rowCount, setRowCount] = useState(-1);
    const [hasNextPage, setHasNextPage] = useState(undefined);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize,
    });
    const paginationMetaRef = useRef();
    const paginationMeta = useMemo(() => {
        if (hasNextPage !== undefined && paginationMetaRef.current?.hasNextPage !== hasNextPage)
            paginationMetaRef.current = { hasNextPage };
        return paginationMetaRef.current;
    }, [hasNextPage]);
    useEffect(() => {
        if (paginationMeta?.hasNextPage !== false)
            setRowCount(-1);
    }, [paginationMeta?.hasNextPage]);
    const [selectedIncident, setSelectedIncident] = useState(undefined);
    const [rowSelectionModel, setRowSelectionModel] = useState({ type: 'include', ids: new Set() });
    useEffect(() => {
        const selectedIds = 'ids' in rowSelectionModel ? Array.from(rowSelectionModel.ids) : rowSelectionModel;
        setSelectedIncident(incidents.find((n) => n.id === selectedIds[0]));
    }, [rowSelectionModel, incidents]);
    const getIncidentsForVOB = (vob, startKey = undefined) => {
        return ConsumerDao.baseQuery({
            TableName: dynamo.tableName,
            IndexName: dynamo.tsiName,
            ScanIndexForward: false,
            ExpressionAttributeNames: {
                '#pk': dynamo.pKey,
                '#sk': dynamo.sKey,
                '#summary': '_summary',
                '#status': '_status',
                '#severity': '_severity',
                '#incidentType': '_incidentType',
                '#timestamp': 'timestamp',
                '#lastUpdated': '_lastUpdated',
                '#closedTimestamp': 'closedTimestamp',
            },
            ExpressionAttributeValues: {
                ':pk': vob.sk,
                ':type': `${IncidentClassName}:`,
            },
            KeyConditionExpression: '#pk = :pk',
            FilterExpression: 'begins_with(#sk, :type)',
            ProjectionExpression: 'sk,#summary,#status,#severity,#incidentType,#timestamp,#lastUpdated,#closedTimestamp',
            Limit: 10,
            ExclusiveStartKey: startKey,
        });
    };
    useEffect(() => {
        ;
        (async () => {
            if (!isValidBusinessObject(matchedPrimary))
                return;
            setLoading(true);
            const { err, data } = await getIncidentsForVOB(matchedPrimary, mapPageToNextCursor.current[paginationModel.page]);
            if (err || !data) {
                setLoading(false);
                return;
            }
            const items = data.Items ?? [];
            const instantiated = items.map((incident) => new Incident({ ...incident, id: incident.sk }));
            const sortedIncidents = instantiated.toSorted((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0));
            setIncidents(sortedIncidents);
            setHasNextPage(data?.LastEvaluatedKey !== undefined);
            setLoading(false);
            // set the start key for the next page
            mapPageToNextCursor.current[paginationModel.page + 1] = data?.LastEvaluatedKey;
        })();
    }, [matchedPrimary?.sk, paginationModel.page]);
    return (_jsxs(Stack, { gap: 1, children: [_jsx(DataGrid, { loading: loading, columns: columns, rows: incidents, pageSizeOptions: [10], hideFooter: paginationModel.page === 0 && !hasNextPage, hideFooterPagination: paginationModel.page === 0 && !hasNextPage, paginationMode: "server", rowCount: rowCount, onRowCountChange: setRowCount, paginationMeta: paginationMeta, paginationModel: paginationModel, onPaginationModelChange: (model) => {
                    if (model.page === 0 || mapPageToNextCursor.current[model.page])
                        setPaginationModel(model);
                }, checkboxSelection: true, hideFooterSelectedRowCount: true, disableMultipleRowSelection: true, rowSelectionModel: rowSelectionModel, onRowSelectionModelChange: setRowSelectionModel }), selectedIncident && (_jsx(IncidentView, { contextCustomer: contextCustomer, user: user, userGroupRoles: userGroupRoles, userSelectedGroup: userSelectedGroup, userFunctions: userFunctions, roleArbitrationInstance: roleArbitrationInstance, transactionFunctions: transactionFunctions, isSmallView: isSmallView, incidentSk: selectedIncident.sk }, selectedIncident.sk))] }));
};
